import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from "@/hooks/useTranslation";
import {
  getPromptContent,
  getPromptStyle,
  getPromptTitle,
  getSupportedLang,
} from "@/lib/prompt-utils";
import { cn } from "@/lib/utils";
import { PromptDTO } from "@shaarp/common";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";

type DialogViewPromptProps = {
  prompt: PromptDTO;
  onClose: () => void;
};

export default function DialogViewPrompt({
  prompt,
  onClose,
}: DialogViewPromptProps) {
  const { t, i18n } = useTranslation();
  const lang = getSupportedLang(i18n.language);
  const { Icon, colors } = getPromptStyle(prompt);
  const title = getPromptTitle(prompt, lang);
  const content = getPromptContent(prompt, lang);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-[600px] w-full">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5">
            <div
              className={cn(
                "flex items-center justify-center rounded-full size-8",
                colors.iconBg,
              )}
            >
              <Icon className={cn("size-4", colors.icon)} />
            </div>
            <span className="truncate">{title}</span>
          </DialogTitle>
        </DialogHeader>
        <Separator />
        <div className="overflow-auto max-h-[50vh] px-1">
          <div className="space-y-2">
            <Label>{t("prompt.view.contentLabel")}</Label>
            <div className="whitespace-pre-wrap text-sm text-muted-foreground leading-relaxed">
              {content}
            </div>
          </div>
        </div>
        <Separator />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">{t("prompt.view.buttons.close")}</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
